import { getSeparatedPhone } from 'legacy/lut/components/widgets/public/phoneEditor/helpers/getSeparatedPhone'

import { IPartner } from 'clickNgo/entities/partner/models'
import { IForm } from 'core-components/classes/models/IForm'

import { mapPartnerLinksFromCrud } from './mapPartnerLinksFromCrud'

const getReporterId = (reporterId: unknown): string[] => {
  if (reporterId) {
    if (typeof reporterId === 'string') {
      return reporterId.split(',').map((item) => item.trim())
    } else {
      return reporterId as string[]
    }
  }

  return []
}

const getPartnerRequestField = (
  data: IForm,
  entryId: string,
  modifiedValues: string[],
  field: string
) => {
  return entryId && modifiedValues.includes(field) ? data[`${field}Ext`] : data[field]
}

export const mapPartnerFromPartnerAllDataRegions = (data: IForm): IPartner => {
  const entryId502 = data.LegalEntryIdExt || data.LegalEntryId || 0

  const isNewPartner =
    !data.PartnerID || data.PartnerID.startsWith('new-') || data.PartnerID.startsWith('portal-')

  const legalModifiedValues = JSON.parse(
    data.LegalModifiedValues || data.LegalModifiedValuesExt || '[]'
  )

  const modifiedValues = data.ModifiedValues ? data.ModifiedValues.split(',') : []

  const { code: phoneCountryCode, phone } = getSeparatedPhone({
    phone: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'MainPhone') || '',
    phoneCode:
      getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'MainPhoneCountryCode') || '',
  })

  const legalState =
    entryId502 && legalModifiedValues.includes('LegalStateProvince')
      ? data['LegalStateExt']
      : data['LegalState']

  return {
    altVendorPC: data.AltVendorPC || '',
    accreditations: data.Accreditations || '',
    addressLine1:
      getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'AddressLine1') || '',
    addressLine2:
      getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'AddressLine2') || '',
    addressLine3:
      getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'AddressLine3') || '',
    altVendorPrint: data.AltVendorPrint || '',
    amplifyImpactCountOfStars: data.AmplifyImpactCountOfStars || 0,
    amplifyImpactPledgeType: data.AmplifyImpactPledgeType || 'CATALYST',
    approverComment: data.ProfileApproverComment || '',
    backupContacts: data.BackupContacts ? data.BackupContacts.split(',') : [],
    bankAccount: data.BankAccount || '',
    bankAccountName: data.BankAccountName || '',
    bankName: data.BankName || '',
    city: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'City') || '',
    communicationName:
      getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'CommunicationName') || '',
    companyDomain: data.CompanyDomain || '',
    companyEnglishName:
      getPartnerRequestField(data, entryId502, legalModifiedValues, 'CompanyEnglishName') || '',
    companyLegalName:
      getPartnerRequestField(data, entryId502, legalModifiedValues, 'CompanyLegalName') || '',
    companyLegalNameNonLatin:
      getPartnerRequestField(data, entryId502, legalModifiedValues, 'CompanyLegalNameNonLatin') ||
      '',
    completeness: data.Completement || 0,
    country: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'Country') || '',
    countryCode: data.CountryCode || '',
    coverageNumber: data.CoverageNumber || '',
    coverageUnit: data.CoverageUnit || '',
    description: data.Description || '',
    eCommerce: data.eCommerce || '',
    eCommerceDetails: data.eCommerceDetails || '',
    ecommerceLinks: mapPartnerLinksFromCrud(data.ECommerceUrls || ''),
    email: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'Email') || '',
    entryId: data.EntryId || data.EntryID || 0,
    entryId458: data.EntryIdAdd || 0,
    entryId406: data.EntryIdExt || 0,
    facebookLink: data.FacebookLink || '',
    gallery: data.Gallery || '',
    hasNotOnlineCommerce: Boolean(data.ECommerceWeDontSellOnline),
    hasAgreements: Boolean(data.AgreementExisting),
    hasActiveAgreement: Boolean(data.IsAP),
    hierarchyType: data.Hierarchy || data.HierarchyType || '',
    hours: data.Hours || '',
    hpdPartnerGuid: data.HPD_Partner_GUID || '',
    hpMemberUnderAmplifySince: data.MembershipDate || '',
    hpMemberSince: data.HPMemberSince || '',
    image: data.Image || '',
    isAmplifyImpactAssessmentSigned: Boolean(data.AmplifyImpactAssessmentSigned),
    isAmplifyImpactEligible: Boolean(data.AmplifyImpactIsEligible),
    isAmplifyImpactMinOneDeal: Boolean(data.AmplifyImpactMinOneDeal),
    isAmplifyImpactPledgeSigned: Boolean(data.AmplifyImpactPledgeSigned),
    isAmplifyMember: Boolean(data.IsAmplifyMember),
    isAWDP: Boolean(data.AWDP),
    isQualified: Boolean(data.QualPrintSupplies),
    isBusinessPartner: false,
    isNewPartner,
    isPolyPartner: Boolean(data.IsPolyPartner),
    isTopPartner: Boolean(data.IsTopPartner),
    isDormantDDQNotified: Boolean(data.DormantDDQNotified),
    isr: data.ISR || '',
    lastUpdatedDate: data.LastUpdatedDate || '',
    lat: data.Lat || 0,
    legalAddressLine1:
      getPartnerRequestField(data, entryId502, legalModifiedValues, 'LegalAddr1') || '',
    legalAddressLine2:
      getPartnerRequestField(data, entryId502, legalModifiedValues, 'LegalAddr2') || '',
    legalAddressLine3:
      getPartnerRequestField(data, entryId502, legalModifiedValues, 'LegalAddr3') || '',
    legalApproverComment: data.LegalApproverCommentExt || '',
    legalCity: getPartnerRequestField(data, entryId502, legalModifiedValues, 'LegalCity') || '',
    legalCountry:
      getPartnerRequestField(data, entryId502, legalModifiedValues, 'LegalCountry') || '',
    entryId502,
    legalModified: data.LegalModified || data.LegalModifiedExt || '',
    legalModifiedBy: data.LegalModifiedBy || data.LegalModifiedByExt || '',
    legalModifiedValues,
    legalProfilingComment: data.LegalProfilingComment || '',
    legalRegion: getPartnerRequestField(data, entryId502, legalModifiedValues, 'LegalRegion') || '',
    legalState: legalState || '',
    legalWorkflowStatus: data.LegalWorkflowStatusExt || data.LegalWorkflowStatus || '',
    legalZipCode:
      getPartnerRequestField(data, entryId502, legalModifiedValues, 'LegalPostalCode') || '',
    link: data.Link || '',
    linkedInLink: data.LinkedInLink || '',
    lng: data.Lng || 0,
    locationId: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'LocationID') || '',
    loyaltyIndex: data.LoyaltyIndex || 0,
    mainContact: data.MainContact || '',
    market: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'Market') || '',
    missedRoles: data.MissedRoles ? data.MissedRoles.split(',') : [],
    modified: data.Modified || '',
    modifiedAdd: data.ModifiedAdd || '',
    modifiedBy: data.ModifiedBy || '',
    modifiedByAdd: data.ModifiedByAdd || '',
    modifiedValues,
    oldPartnerId: '',
    organization: data.Organization,
    parentLocationId: data.ParentLocationID || '',
    parentPartnerId: data.ParentPartnerID || '',
    partnerAgrees: data.PartnerAgrees || 0,
    partnerCommunication: data.PartnerCommunication || '',
    partnerId: data.PartnerID || '',
    partnerSince: data.PartnerSince || '',
    partnerType: data.PartnerType || '',
    partnerTypeStartDate: data.PartnerType_StartDate || '',
    phone,
    phoneCountryCode,
    profileTeamComment: data.ProfileTeamComment || '',
    profileUpdateDate: data.Profile_UpdateDate || '',
    programCodes: data.ProgramCodes ? data.ProgramCodes.split(',') : [],
    region: data.Region || '',
    registrationNumber: data.RegistrationNumber || 0,
    reporterId: getReporterId(data.ReporterID),
    resellerAbInd: data.RESELLER_AB_IND || '',
    responseTimeNumber: data.ResponseTimeNumber || 0,
    responseTimeUnits: data.ResponseTimeUnits || '',
    rplStatus: data.RPL_STATUS || '',
    serviceCoverage: data.ServiceCoverage || 0,
    shouldUpdateInMetalocator: data.NeedUpdateInMetalocator || false,
    socialMediaLinks: mapPartnerLinksFromCrud(
      getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'SocialMediaUrls') || ''
    ),
    source: data.Source || '',
    sourceRequester: data.SourceRequestor || '',
    splPriceFlag: data.SPL_PRICE_FLAG || '',
    splPriceOverride: data.SPL_PRICE_OVERIDE || '',
    stage: data.STAGE || '',
    state: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'State') || '',
    status: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'Status') || '',
    stocking: data.Stocking || '',
    storeNumber: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'StoreNumber') || '',
    tagIdList: data.TagIdList ? data.TagIdList.split(',') : [],
    taxId: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'TaxID') || '',
    territoryExtensionConducted: data.TerritoryExtensionConducted || 0,
    territoryExtensionsBusinessJustification: data.TerritoryExtensionsBusinessJustification || '',
    territoryExtensionsList: data.TerritoryExtensionsList || '',
    topPartnerId:
      getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'TopPartnerID') || '',
    twitterLink: data.TwitterLink || '',
    url: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'URL') || '',
    whatsAppLink: data.WhatsAppLink || '',
    workflowStatus: data.WorkflowStatus || '',
    zipCode: getPartnerRequestField(data, data.EntryIdExt, modifiedValues, 'ZIPCode') || '',
    zipCodeCovered: data.ZipCodeCovered || 0,
    zipCodes: data.ZipCodes ? data.ZipCodes.split(',') : [],
    onboardingRequestId: data.OnboardingRequestId || null,
  }
}
